import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "src/hooks/reactRedux";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import {
  setProvincesAction,
  setDistrictsAction,
  setMunicipalitiesAction,
  setWardsAction,
  setHazardTypesAction,
  setEnumOptionsAction,
  setAuthAction,
  setUserDetailAction,
  SetAdminMenuAction,
} from "#actionCreators";
import { mapStyleSelector, authStateSelector } from "#selectors";
import { getAuthState } from "#utils/session";
import { sanitizeResponse } from "#utils/common";
import Multiplexer from "../Multiplexer";

import * as PageTypes from "#store/atom/page/types";
import { User } from "#store/atom/auth/types";
import { ModelEnum } from "#types";

const App = () => {
  const dispatch = useAppDispatch();
  const mapStyle = useAppSelector(mapStyleSelector);
  const authState = useAppSelector(authStateSelector);

  // Set auth state on mount
  useEffect(() => {
    const authState = getAuthState();
    dispatch(setAuthAction(authState));
  }, [dispatch]);

  // Configure axios baseURL based on subdomain
  useEffect(() => {
    const wsEndpoint = import.meta.env.VITE_APP_API_SERVER_URL;
    const domain = import.meta.env.VITE_APP_DOMAIN;

    if (!wsEndpoint || !domain) return;

    const { hostname } = window.location;
    const index = hostname.search(`.${domain}`);
    const subDomain = index !== -1 ? hostname.substring(0, index) : undefined;

    let baseURL = wsEndpoint;
    if (subDomain) {
      const escapedDomain = domain.replace(/\./g, "\\.");
      baseURL = wsEndpoint.replace(
        new RegExp(escapedDomain),
        `${subDomain}.${domain}`
      );
    }

    axios.defaults.baseURL = baseURL;
    axios.defaults.headers.common["Accept"] = "application/json";
  }, []);

  // Province query with caching
  const {
    data: provincesResponse,
    isLoading: provincePending,
    isError: provinceResponseError,
  } = useQuery({
    queryKey: ["provinces"],
    queryFn: async () => {
      const response = await axios.get("/province/");
      return sanitizeResponse(response.data);
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (provincesResponse) {
      const { results = [], data = [] } = provincesResponse as {
        results?: PageTypes.Province[];
        data?: PageTypes.Province[];
      };
      const provinces = results.length > 0 ? results : data;
      dispatch(setProvincesAction({ provinces }));
    }
  }, [provincesResponse, dispatch]);

  // District query with caching
  const {
    data: districtsResponse,
    isLoading: districtPending,
    isError: districtResponseError,
  } = useQuery({
    queryKey: ["districts"],
    queryFn: async () => {
      const response = await axios.get("/district/");
      return sanitizeResponse(response.data);
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (districtsResponse) {
      const { results = [], data = [] } = districtsResponse as {
        results?: PageTypes.District[];
        data?: PageTypes.District[];
      };
      const districts = results.length > 0 ? results : data;
      dispatch(setDistrictsAction({ districts }));
    }
  }, [districtsResponse, dispatch]);

  // Municipality query with caching
  const {
    data: municipalitiesResponse,
    isLoading: municipalityPending,
    isError: municipalityResponseError,
  } = useQuery({
    queryKey: ["municipalities"],
    queryFn: async () => {
      const response = await axios.get("/municipality/");
      return sanitizeResponse(response.data);
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (municipalitiesResponse) {
      const { results = [], data = [] } = municipalitiesResponse as {
        results?: PageTypes.Municipality[];
        data?: PageTypes.Municipality[];
      };
      const municipalities = results.length > 0 ? results : data;
      dispatch(setMunicipalitiesAction({ municipalities }));
    }
  }, [municipalitiesResponse, dispatch]);

  // Ward query with caching
  const {
    data: wardsResponse,
    isLoading: wardListPending,
    isError: wardListResponseError,
  } = useQuery({
    queryKey: ["wards"],
    queryFn: async () => {
      const response = await axios.get("/ward/", { params: { limit: -1 } });
      return sanitizeResponse(response.data);
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (wardsResponse) {
      const { results = [], data = [] } = wardsResponse as {
        results?: PageTypes.Ward[];
        data?: PageTypes.Ward[];
      };
      const wards = results.length > 0 ? results : data;
      dispatch(setWardsAction({ wards }));
    }
  }, [wardsResponse, dispatch]);

  // Hazard types query with caching
  const {
    data: hazardTypesResponse,
    isLoading: hazardTypePending,
    isError: hazardTypeResponseError,
  } = useQuery({
    queryKey: ["hazardTypes"],
    queryFn: async () => {
      const response = await axios.get("/hazard/");
      return sanitizeResponse(response.data);
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (hazardTypesResponse) {
      const { results = [], data = [] } = hazardTypesResponse as {
        results?: PageTypes.HazardType[];
        data?: PageTypes.HazardType[];
      };
      const hazardTypes = results.length > 0 ? results : data;
      dispatch(setHazardTypesAction({ hazardTypes }));
    }
  }, [hazardTypesResponse, dispatch]);

  // Enum options query with caching
  const {
    data: enumOptionsResponse,
    isLoading: enumPending,
    isError: enumError,
  } = useQuery({
    queryKey: ["enumOptions"],
    queryFn: async () => {
      const response = await axios.get("/enum-choice/");
      return sanitizeResponse(response.data);
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (enumOptionsResponse) {
      const enumList: ModelEnum[] = enumOptionsResponse as ModelEnum[];
      dispatch(setEnumOptionsAction({ enumList }));
    }
  }, [enumOptionsResponse, dispatch]);

  // Admin menu query with caching
  const {
    data: menuResponse,
    isLoading: menuPending,
    isError: menuError,
  } = useQuery({
    queryKey: ["adminMenu"],
    queryFn: async () => {
      const response = await axios.get("/adminportal-menu/");
      return sanitizeResponse(response.data);
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (menuResponse) {
      dispatch(SetAdminMenuAction(menuResponse.results));
    }
  }, [menuResponse, dispatch]);

  // Me query (conditional on authentication) with caching
  const {
    data: meResponse,
    isLoading: mePending,
    isError: meResponseError,
  } = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const response = await axios.get("/me/");
      return sanitizeResponse(response.data);
    },
    enabled: authState.authenticated,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (meResponse) {
      dispatch(setUserDetailAction(meResponse as User));
    }
  }, [meResponse, dispatch]);

  // Aggregate pending and error states
  const pending =
    provincePending ||
    districtPending ||
    municipalityPending ||
    wardListPending ||
    hazardTypePending ||
    enumPending ||
    menuPending ||
    mePending;

  const hasError =
    provinceResponseError ||
    districtResponseError ||
    municipalityResponseError ||
    wardListResponseError ||
    hazardTypeResponseError ||
    enumError ||
    menuError ||
    meResponseError;

  return (
    <Multiplexer pending={pending} hasError={hasError} mapStyle={mapStyle} />
  );
};

export default App;
